import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoIcon from "@mui/icons-material/Info";
import MenuIcon from "@mui/icons-material/Menu";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { CircularProgress } from "@mui/material";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router";
import { AccessOutcome, useClientAccess } from "../api/useClientAccess";
import { useImplementationGuide } from "../api/useImplementationGuide";
import ClientNavigation from "../components/ClientNavigation/ClientNavigation";
import DownloadIndicator from "../components/DownloadIndicator/DownloadIndicator";
import { useModal } from "../components/Modal/useModal";
import NavButton from "../components/Navigation/NavButton";
import Navigation from "../components/Navigation/Navigation";
import { useClientConfiguration, useClientPrintConfiguration } from "../data/global/useProductConfiguration";
import { useQueueState } from "../data/queue/hooks";
import { logoutWithCleanup } from "../env";
import { useAuth } from "../hooks/useAuth";
import useGetRole, { FRM_ROLE } from "../hooks/useGetRole";
import useHasGeoAccess from "../hooks/useHasGeoAccess";
import useHasHcpAccessAsFrm from "../hooks/useHasHcpAccessAsFrm";
import useWindowSize, { LayoutSize } from "../hooks/useWindowSize";
import { useClientRoutesAndLabels } from "../ui/routes";
const Loading: React.FC = () => {
  return (
    <div className="flex h-screen justify-center items-center">
      <CircularProgress size={80} />
    </div>
  );
};

const DefaultLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [t] = useTranslation();
  const { logout } = useAuth();
  const { setScrollRef } = useModal();
  const layoutSize = useWindowSize();

  const { isLoading, data: clientAccess } = useClientAccess();
  const clients = clientAccess?.clients || [];
  const { client: selectedClient } = useParams() as { client: string };
  const { data: role, isFetched } = useGetRole(selectedClient);
  const routes = useClientRoutesAndLabels(selectedClient);
  const isFRM = !isFetched || String(role) === FRM_ROLE;
  const isValidClient = clients?.some((client) => client === selectedClient);
  const printConfig = useClientPrintConfiguration(selectedClient);
  const { howToVideo } = useClientConfiguration(selectedClient);
  const { data: implementationGuideUrl } = useImplementationGuide(selectedClient);
  useHasGeoAccess(selectedClient);
  const hasHcpAccessAsFrm = useHasHcpAccessAsFrm(selectedClient, isFRM);
  //track if nav is expanded when in mobile view
  const [expanded, setExpanded] = useState(false);
  const closeNav = () => {
    setExpanded(false);
  };

  //setup mainContent ref so bg doesn't scroll when modal is open
  const mainContentRef = useRef<null | HTMLDivElement>(null);
  useEffect(() => {
    setScrollRef(mainContentRef);
  }, [setScrollRef, mainContentRef]);

  const { queue } = useQueueState();

  const [isNearTop, setIsNearTop] = useState(true);
  const scrollTop = () => {
    window.scroll({ top: 0, left: 0, behavior: "smooth" });
  };

  const handleScroll = () => {
    setIsNearTop(window.scrollY <= 200);
  };

  useEffect(() => {
    document.addEventListener("scroll", handleScroll);
    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const expandableNavClasses = classNames(
    "overflow-hidden duration-500 flex-grow lg:flex justify-between items-center",
    {
      "max-h-96": expanded,
    },
    {
      "max-h-0 lg:max-h-16": !expanded,
    },
  );

  //Prevent background from scrolling if modal is open, and prevent screenshift by scrollbar
  const appBarClasses = classNames("text-white bg-slate-gray lg:flex justify-between items-center fixed w-full z-30");
  const bodyClasses = classNames("px-6 lg:container pt-14");
  if (isLoading) {
    return <Loading />;
  }

  if (clientAccess?.accessOutcome !== AccessOutcome.HasAccessWithData || clients.length === 0) {
    return <Navigate to={routes.noAccess.path} replace />;
  }

  if (!selectedClient || !isValidClient) {
    return <Navigate to={routes.path} replace />;
  }

  return (
    <>
      <nav className={appBarClasses}>
        <div className="px-4 header-5-regular flex justify-between items-center">
          <ClientNavigation closeNav={closeNav} />
          <div className="flex items-center">
            {layoutSize === LayoutSize.Mobile && (
              <div className="text-white lg:hidden">
                <DownloadIndicator />
              </div>
            )}
            <button className="block lg:hidden p-2 -mr-3" onClick={() => setExpanded(!expanded)}>
              {!expanded && <MenuIcon fontSize="large" />}
              {expanded && <CloseIcon fontSize="large" />}
            </button>
          </div>
        </div>
        {(!isFRM || hasHcpAccessAsFrm) && <div className="border-b lg:border-r lg:h-6 border-secondary"></div>}
        <div className={expandableNavClasses}>
          {(!isFRM || hasHcpAccessAsFrm) && <Navigation closeNav={closeNav} hasHcpAccessAsFrm={hasHcpAccessAsFrm} />}
          {layoutSize === LayoutSize.Desktop && (
            <div className="text-white hidden lg:block">
              <DownloadIndicator />
            </div>
          )}
          {!isFRM && <div className="border-b lg:border-r lg:h-6 border-secondary"></div>}
          <div className="flex lg:px-4 pb-1 lg:pb-0 space-x-2 justify-center">
            {(printConfig.hcpPrintMessages || printConfig.resourceLibrary) &&
              (!isFRM || printConfig.printEnabledForFRM) && (
                <NavButton
                  onClick={closeNav}
                  to={routes.client.print.queue.with({ client: selectedClient })}
                  title={t("common.printQueue")}
                  className={({ isActive }) => `nav px-3${isActive ? " active-nav" : ""}`}
                >
                  <>
                    <PrintOutlinedIcon />
                    {queue.length > 0 && <div className="queue-notification">{queue.length}</div>}
                  </>
                </NavButton>
              )}
            {howToVideo && (
              <NavButton
                onClick={closeNav}
                to={routes.client.howTo.with({ client: selectedClient })}
                title={t("howTo.navIconTitle")}
                className={({ isActive }) => `nav px-3${isActive ? " active-nav" : ""}`}
              >
                <InfoIcon />
              </NavButton>
            )}
            {implementationGuideUrl && (
              <NavButton
                onClick={closeNav}
                to={routes.client.implementationGuide.with({ client: selectedClient })}
                title={t("implementationGuide.navIconTitle")}
                aria-label={t("implementationGuide.navIconTitle")}
                className={({ isActive }) => `nav px-3${isActive ? " active-nav" : ""}`}
              >
                <DescriptionOutlinedIcon />
              </NavButton>
            )}
            <NavButton className="mt-3 pb-3 pl-3" onClick={() => logoutWithCleanup(logout)}>
              {t("common.logout")}
            </NavButton>
          </div>
        </div>
      </nav>
      <main ref={mainContentRef} id="main-content" className="w-full">
        <div className={bodyClasses}>{children}</div>
      </main>
      {!isNearTop && (
        <button
          onClick={scrollTop}
          className="hidden xl:block bg-primary fixed right-0 bottom-0 mr-6 mb-6 text-white rounded-full p-2"
        >
          <ArrowUpwardIcon />
        </button>
      )}
    </>
  );
};

export default DefaultLayout;
